/**
 * FC.05 — CotejoCompletitudService
 *
 * Orquestador principal del cotejo de completitud PTEL.
 * Conecta: InventarioReferenciaBuilder → MatchingEngine → CriticidadNormativa
 * para producir un ResultadoCotejo completo con estadísticas y clasificación legal.
 *
 * ★ CHECKPOINT CP-1: Backend cotejo funcional E2E.
 *
 * @module services/cotejo/CotejoCompletitudService
 * @version 1.0.0
 */

import type { ProcessedInfrastructure } from '../../types/processing';
import type { InfrastructureType } from '../../types/infrastructure';
import type {
  CandidatoReferencia,
  ResultadoCotejo,
  InfraAusente,
  InfraSobrante,
  EstadisticasCotejo,
  EstadisticaTipologia,
  OpcionesCotejo,
} from './types';
import { crearOpcionesCotejo } from './types';
import { MatchingEngine } from './MatchingEngine';
import type { MatchingResult } from './MatchingEngine';
import type { InventarioReferenciaBuilder } from './InventarioReferenciaBuilder';
import { getReferenciaNormativa, esObligatoria } from './CriticidadNormativa';

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class CotejoCompletitudService {
  private readonly builder: InventarioReferenciaBuilder;
  private readonly engine = new MatchingEngine();

  constructor(builder: InventarioReferenciaBuilder) {
    this.builder = builder;
  }

  /**
   * Ejecuta el cotejo completo de un municipio.
   *
   * Flujo:
   * 1. Builder → inventario de referencia por tipología
   * 2. Engine  → matching PTEL ↔ referencia (scoring compuesto)
   * 3. Criticidad → clasificación legal de ausencias
   * 4. Estadísticas → contadores y ratios por tipología
   */
  async ejecutarCotejo(
    infrasPTEL: ProcessedInfrastructure[],
    ine5: string,
    municipio: string,
    opciones?: Partial<OpcionesCotejo>,
  ): Promise<ResultadoCotejo> {
    const opts = crearOpcionesCotejo(opciones);

    // 1. Construir inventario de referencia
    const inventario = await this.builder.build(ine5, opts.tipologias);

    // Aplanar candidatos para el matching global
    const todosLosCandidatos: CandidatoReferencia[] = [];
    for (const candidatos of inventario.values()) {
      todosLosCandidatos.push(...candidatos);
    }

    // `ALBCOT`: sin enriquecimiento de capacidad. El antiguo `EnriquecedorMetadatos`
    // (FC.06b) anotaba camas, alumnos y aforos de tablas fijas sin fuente, con valor
    // por defecto para cualquier elemento del tipo; se borró entero. Los candidatos
    // conservan solo la `metadata` con fuente que pone `InventarioReferenciaBuilder`
    // (m³ de DERA G3, BIC, potencia). La capacidad de albergue llegará con `ALBCAP`.

    // 2. Matching PTEL ↔ referencia
    const matching = this.engine.match(infrasPTEL, todosLosCandidatos, opts);

    // 3. Clasificar ausencias con criticidad normativa
    const ausentes = this.clasificarAusentes(matching.ausentes);

    // 4. Clasificar sobrantes
    const sobrantes: InfraSobrante[] = (opts.incluirSobrantes !== false)
      ? matching.sobrantes.map(infra => ({ infraPTEL: infra, estado: 'sobrante' as const }))
      : [];

    // 5. Calcular estadísticas
    const estadisticas = this.calcularEstadisticas(
      infrasPTEL, todosLosCandidatos, matching, ausentes, sobrantes, inventario,
    );

    return {
      ine5,
      municipio,
      timestamp: new Date().toISOString(),
      matches: matching.matches,
      ausentes,
      sobrantes,
      estadisticas,
      opciones: opts,
    };
  }

  // ===========================================================================
  // CLASIFICACIÓN DE AUSENCIAS
  // ===========================================================================

  private clasificarAusentes(candidatosSinMatch: CandidatoReferencia[]): InfraAusente[] {
    return candidatosSinMatch.map(candidato => {
      const ref = getReferenciaNormativa(candidato.tipologia);
      const obligatoria = esObligatoria(candidato.tipologia);

      return {
        candidato,
        estado: obligatoria ? 'ausente_critico' as const : 'ausente_recomendado' as const,
        criticidad: ref.criticidad,
        referenciaNormativa: ref,
      };
    });
  }

  // ===========================================================================
  // ESTADÍSTICAS
  // ===========================================================================

  private calcularEstadisticas(
    infrasPTEL: ProcessedInfrastructure[],
    todosLosCandidatos: CandidatoReferencia[],
    matching: MatchingResult,
    ausentes: InfraAusente[],
    sobrantes: InfraSobrante[],
    inventario: Map<InfrastructureType, CandidatoReferencia[]>,
  ): EstadisticasCotejo {
    const incluidos = matching.matches.filter(m => m.estado === 'incluido').length;
    const probables = matching.matches.filter(m => m.estado === 'probable').length;
    const ausentesCriticos = ausentes.filter(a => a.estado === 'ausente_critico').length;
    const ausentesRecomendados = ausentes.filter(a => a.estado === 'ausente_recomendado').length;

    // Desglose por tipología
    const tipologias = new Set<InfrastructureType>();
    for (const [tipo] of inventario) tipologias.add(tipo);
    for (const m of matching.matches) tipologias.add(m.candidato.tipologia);

    const porTipologia: EstadisticaTipologia[] = [...tipologias].map(tipo => {
      const totalReferencia = inventario.get(tipo)?.length ?? 0;
      const matchesTipo = matching.matches.filter(m => m.candidato.tipologia === tipo);
      const ausentesTipo = ausentes.filter(a => a.candidato.tipologia === tipo);
      const sobrantesTipo = sobrantes.filter(s => {
        // Intentar clasificar sobrante por su tipo
        const norm = s.infraPTEL.tipo?.toUpperCase() ?? '';
        return norm === tipo || norm.includes(tipo);
      });

      const incluidosTipo = matchesTipo.length;
      const ratioCobertura = totalReferencia > 0 ? incluidosTipo / totalReferencia : 1;

      return {
        tipologia: tipo,
        totalPTEL: incluidosTipo + sobrantesTipo.length,
        totalReferencia,
        incluidos: incluidosTipo,
        ausentes: ausentesTipo.length,
        sobrantes: sobrantesTipo.length,
        ratioCobertura: Math.round(ratioCobertura * 100) / 100,
      };
    });

    const totalRef = todosLosCandidatos.length;
    const totalMatched = matching.matches.length;
    const ratioCoberturaGlobal = totalRef > 0
      ? Math.round((totalMatched / totalRef) * 100) / 100
      : 1;

    return {
      totalPTEL: infrasPTEL.length,
      totalReferencia: totalRef,
      incluidos,
      probables,
      ausentesCriticos,
      ausentesRecomendados,
      sobrantes: sobrantes.length,
      porTipologia,
      ratioCoberturaGlobal,
    };
  }
}
